import { GraphBuilder } from '../graph/builder';
import { ComponentDef } from './types';

export const prefillDecodeComponent: ComponentDef = {
  id: 'prefill-decode',
  name: 'Prefill & Decode',
  description: 'Separate prompt processing (prefill) from token-by-token generation (decode) with KV cache reuse.',
  category: 'optimization',
  params: [
    { name: 'd_model', label: 'Model dimension', type: 'number', default: 512, min: 64, max: 4096, step: 64 },
    { name: 'prefill_len', label: 'Prefill length', type: 'number', default: 512, min: 1, max: 8192, step: 1 },
    { name: 'decode_len', label: 'Decode tokens', type: 'number', default: 64, min: 1, max: 2048, step: 1 },
    {
      name: 'variant',
      label: 'View',
      type: 'select',
      default: 'overview',
      options: [
        { label: 'Overview', value: 'overview' },
        { label: 'Roofline Model', value: 'roofline' },
        { label: 'Optimization', value: 'optimization' }
      ]
    }
  ],
  buildGraph: (params) => {
    const d_model = Number(params.d_model);
    const prefill_len = Number(params.prefill_len);
    const decode_len = Number(params.decode_len);
    const variant = String(params.variant || 'overview');

    const g = new GraphBuilder();

    if (variant === 'roofline') {
      // Roofline Model: prefill = compute-bound (high FLOPs), decode = memory-bound (low FLOPs, KV reads)
      g.input('prompt', [prefill_len, d_model]);

      // Prefill phase: compute-bound (matrix multiplies, high arithmetic intensity)
      g.linear('prefill_qkv', 'prompt', [d_model, d_model]);
      g.scale('prefill_intensity', 'prefill_qkv', 'Compute Intensity: O(n·d²) FLOPs');
      g.cache('kv_cache', 'prefill_qkv', [prefill_len, d_model]);

      // Decode phase: memory-bound (KV cache reads dominate, low arithmetic intensity)
      g.input('token', [decode_len, d_model]);
      g.linear('decode_qkv', 'token', [d_model, d_model]);
      g.concat('decode_kv', ['kv_cache', 'decode_qkv'], [prefill_len + decode_len, d_model]);
      g.scale('decode_bandwidth', 'decode_kv', 'Memory Bandwidth: O(n·d) reads/step');

      // Roofline comparison
      g.scale('roofline_prefill', 'prefill_intensity', 'Prefill: Compute-Bound');
      g.scale('roofline_decode', 'decode_bandwidth', 'Decode: Memory-Bound');
      g.output('out', 'roofline_decode');

    } else if (variant === 'optimization') {
      // Optimization techniques for each phase
      g.input('prompt', [prefill_len, d_model]);

      // Prefill optimizations
      g.linear('prefill_qkv', 'prompt', [d_model, d_model]);
      g.activation('flash_attn', 'prefill_qkv', 'Flash Attention (IO-aware tiling)');
      g.activation('chunked_prefill', 'flash_attn', 'Chunked Prefill (split long context)');
      g.cache('kv_cache', 'chunked_prefill', [prefill_len, d_model]);

      // Decode optimizations
      g.input('token', [decode_len, d_model]);
      g.linear('decode_qkv', 'token', [d_model, d_model]);
      g.concat('decode_kv', ['kv_cache', 'decode_qkv'], [prefill_len + decode_len, d_model]);
      g.activation('spec_decode', 'decode_kv', 'Speculative Decoding (draft+verify)');
      g.activation('cont_batch', 'spec_decode', 'Continuous Batching (dynamic scheduling)');

      g.linear('out', 'cont_batch', [d_model, d_model]);
      g.output('out', 'out');

    } else {
      // Overview: prefill + decode phases (original implementation)
      g.input('prompt', [prefill_len, d_model]);

      g.linear('prefill_qkv', 'prompt', [d_model, d_model]);
      g.cache('kv_cache', 'prefill_qkv', [prefill_len, d_model]);
      g.scale('prefill', 'kv_cache', 'Prefill');

      g.input('token', [decode_len, d_model]);
      g.linear('decode_qkv', 'token', [d_model, d_model]);
      g.concat('decode_kv', ['kv_cache', 'decode_qkv'], [prefill_len + decode_len, d_model]);
      g.scale('decode', 'decode_kv', 'Decode');
      g.linear('out', 'decode', [d_model, d_model]);
      g.output('out', 'out');
    }

    return g.build();
  }
};
